import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Brain, ArrowLeft, Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useFormValidation } from '../hooks/useFormValidation';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { errors, validateField, validateAll } = useFormValidation<{
    email: string;
    password: string;
  }>({
    email: (value) => {
      if (!value) return t('validation.emailRequired');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return t('validation.emailInvalid');
      return null;
    },
    password: (value) => {
      if (!value) return t('validation.passwordRequired');
      return null;
    },
  });
  const { signIn } = useAuth();
  const navigate = useNavigate();

  // Remove dashboard-active class to ensure scroll works
  useEffect(() => {
    document.body.classList.remove('dashboard-active');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.height = '';

    return () => {
      document.body.classList.remove('dashboard-active');
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Run all validations
    const isValid = validateAll({ email, password });
    if (!isValid) {
      return;
    }

    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <>
      <SEO
        title={t('login.title')}
        description={t('login.seoDescription')}
        canonical="/login"
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-hero relative overflow-hidden flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(262.1 83.3% 57.8%) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full relative z-10"
      >
        {/* Login Card */}
        <div className="bg-card rounded-2xl shadow-card border border-border/50 p-6">
          {/* Badge */}
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 inline-flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
              <span className="text-sm font-semibold text-primary">{t('login.title')}</span>
            </div>
          </div>

          {/* Header with Logo */}
          <div className="text-center mb-6">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-soft">
                <Brain className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-2xl font-bold text-foreground">Psymetrika</span>
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
              id="login-error"
            >
              <p className="text-red-800 text-sm">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const nextEmail = e.target.value;
                    setEmail(nextEmail);
                    if (errors.email) validateField('email', nextEmail, { email: nextEmail, password });
                  }}
                  onBlur={(e) => validateField('email', e.target.value, { email: e.target.value, password })}
                  required
                  aria-invalid={!!errors.email || !!error}
                  aria-describedby={errors.email ? 'email-error' : error ? 'login-error' : undefined}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground ${errors.email ? 'border-red-500' : 'border-border'}`}
                  placeholder="tu@email.com"
                />
              </div>
              {errors.email && (
                <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    const nextPassword = e.target.value;
                    setPassword(nextPassword);
                    if (errors.password) validateField('password', nextPassword, { email, password: nextPassword });
                  }}
                  onBlur={(e) => validateField('password', e.target.value, { email, password: e.target.value })}
                  required
                  aria-invalid={!!errors.password || !!error}
                  aria-describedby={errors.password ? 'password-error' : error ? 'login-error' : undefined}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground ${errors.password ? 'border-red-500' : 'border-border'}`}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-600" role="alert">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="gradient"
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('login.signingIn')}
                </>
              ) : (
                t('login.submit')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {t('login.noAccount')}{' '}
              <Link to="/signup" className="text-primary hover:text-primary/80 font-medium">
                {t('login.signUp')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center text-sm text-muted-foreground"
        >
          <p>
            {t('login.acceptLoginTerms')}{' '}
            <Link to="/terms" className="text-primary hover:underline" target="_blank">
              {t('signup.termsOfService')}
            </Link>{' '}
            {t('signup.and')}{' '}
            <Link to="/privacy" className="text-primary hover:underline" target="_blank">
              {t('signup.privacyPolicy')}
            </Link>
          </p>
        </motion.div>
      </motion.div>
      </div>
    </>
  );
}
